import {Component, ViewChild} from "@angular/core";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {AppSettingsService} from "../app-settings/app-settings.service";
import {CreditCheckService} from "./credit-check.service";
import {CreditCheckReportHistory} from "./credit-check.model";
import {RouteHelper, RouteMapping} from "../misc/route-helper";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {ConfirmDialogService} from "../confirm-dialog/confirm-dialog-service";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  selector: "credit-check",
  templateUrl: "./credit-request-history-report.template.pug",
  providers: [
    CreditCheckService,
    ConfirmDialogService,
  ],
})

export class CreditRequestHistoryReportComponent extends RouteHelper {
  error: Error;
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  filter: any = {
    country: null,
    creditAnalyst: null,
    status: null,
    maStartDate: null,
    maEndDate: null,
  };

  countries: any = [];
  creditAnalyst: any;
  status: any = [
    {
      id: true,
      value: "Released",
    },
    {
      id: false,
      value: "Blocked",
    },
  ]

  creditCheckList: CreditCheckReportHistory[];
  expandedCreditCheckData: string = "";
  selectedCreditCheckList: any = [];
  public paramsMap: RouteMapping[] = [
    {fieldName: "currentPage", paramName: "page", defaultValue: DEFAULT_PAGE, type: "number"} as RouteMapping,
    {fieldName: "pageSize", paramName: "limit", defaultValue: DEFAULT_PAGE_LIMIT, type: "number"} as RouteMapping,
    {fieldName: "searchText", paramName: "search"} as RouteMapping,
  ];

  private creditReleasedMessages: string = "";
  private validationMessages: string = "";
  enableHighRadius: boolean = false;
  loading: boolean = false;
  @ViewChild("creditReportStartDate") creditReportStartDate;
  @ViewChild('creditReportEndDate') creditReportEndDate;
  constructor(
    public dialogService: DialogService,
    public appSettingsService: AppSettingsService,
    public creditCheckService: CreditCheckService,
    public route: ActivatedRoute,
    public router: Router,
    private confirmDialogService: ConfirmDialogService,
  ) {
    super();
  }

  toggleExpandedContractAccount(caName) {
    if (this.expandedCreditCheckData === caName) {
      this.closeExpandedCaHelper();
    } else {
      this.expandedCreditCheckData = caName;
    }
  }

  async loadData() {
    this.loading = true;
    this.enableHighRadius = await this.appSettingsService.getFeaturePermission(
      "enableHighRadius",
    );
    this.creditReleasedMessages = "";
    const {rows: creditAnalystData} = await this.creditCheckService.getCreditAnalyst();
    this.creditAnalyst = creditAnalystData;
    this.appSettingsService.contactCountries.subscribe((contactCountries) => {
      for (const country of contactCountries) {
        this.countries.push({id: country.countryId, value: country.countryName});
      }
    });

    const {rows, count} = await this.creditCheckService.getCreditCheckRequestReport(
      (+this.currentPage) - 1,
      (+this.pageSize),
      this.searchText,
      this.filter,
    );
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;
    this.creditCheckList = rows;
    this.creditCheckList.forEach((creditCheck) => {
      creditCheck.tcvFormatted = !Number.isNaN(creditCheck.tcv) ? Number(creditCheck.tcv).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
    });
    this.loading = false;
  }

  public navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["credit-request-history-report"], {queryParams, skipLocationChange});
  }

  private closeExpandedCaHelper() {
    this.expandedCreditCheckData = "";
  }

  public onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
    this.syncRoute();
  }

  getStatus(status) {
    return status ? "Released" : "Blocked";
  }

  async save() {
    this.loading = true;
    this.creditReleasedMessages = "";
    if (this.filter && this.filter.maStartDate && this.filter.maEndDate &&
      this.filter.maStartDate > this.filter.maEndDate) {
      this.validationMessages = "Start date cannot be after end date.";
    } else {
      this.validationMessages = "";
      const {rows, count} = await this.creditCheckService.getCreditCheckRequestReport(
        (+this.currentPage) - 1,
        (+this.pageSize),
        this.searchText,
        this.filter,
      );
      this.maxPages = Math.ceil(count / (+this.pageSize));
      this.totalCount = count;
      this.creditCheckList = rows;
    }
    this.loading = false;
  }

  async clearFilter() {
    this.creditReleasedMessages = "";
    this.validationMessages = "";
    this.filter = {
      country: null,
      creditAnalyst: null,
      status: null,
      maStartDate: null,
      maEndDate: null,
    };
    this.searchText = null;
    this.creditReportStartDate.clearDate();
    this.creditReportEndDate.clearDate();
    this.loadData();
  }

  onCheckboxChange(event: any) {
    if (event.target.checked) {
      this.selectedCreditCheckList.push(event.target.value);
    } else {
      const index = this.selectedCreditCheckList
        .findIndex(x => x.value === event.target.value);
      this.selectedCreditCheckList.splice(index, 1);
    }
  }

  async release() {
    if(this.selectedCreditCheckList.length){
      const message = `Are you sure you want to release the credit block?`;
      const result = await this.confirmDialogService.confirm("Confirm", message);
      if (result) {
        this.loading = true;
        try {
          const response = await this.creditCheckService.creditCheckRequestRelease(
            this.selectedCreditCheckList,
          );
          if (response.releasedCreditCheckList && response.releasedCreditCheckList.length) {
            await this.loadData();
            this.creditReleasedMessages = "Credit Check Released.";
          }
          this.loading = false;
        } catch (err) {
          await this.loadData();
          this.error = err;
          this.loading = false;
        }
        this.selectedCreditCheckList = [];
      } else {
        this.creditReleasedMessages = "";
      }
    }else{
      const message = `We have not received any credit requests for release`;
      await this.confirmDialogService.confirm("Message", message,);
    }
  }
}
